import { convertFullWidthToHalfWidth } from "@/modules/irohana-components/utils/pieces";
import * as wanakana from "wanakana";
import Moji from "moji";

export const handleKatakanaToFurigana = (value: string) => {
  return wanakana.toHiragana(value, { passRomaji: true });
};

export const convertNumberToZipCode = (value: string) => {
  if (!value) return "";

  const cleanedValue = value.replace(/[^\d０-９]/g, "");

  if (cleanedValue.length !== 7) return value;

  return `${cleanedValue.slice(0, 3)}-${cleanedValue.slice(3)}`;
};

export const convertZipCodeToNumber = (zipCode: string) => {
  if (!zipCode) return;
  return zipCode.replace("-", "");
};

export const convertNumberToTelNumber = (value: string): string => {
  if (!value) return "";

  // Only format when input has enough digits for a valid tel number (10+ digits)
  if (value.length < 10) return value;

  return `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7)}`;
};

export const convertTelNumberToNumber = (value: string): string | undefined => {
  if (!value) return undefined;
  return value.replace(/-/g, "");
};

export const convertNumberToCommas = (value: string) => {
  if (value) {
    const normalizedValue = convertFullWidthToHalfWidth(value.toString());
    if (/^-?[0-9]+(\.[0-9]*)?$/.test(normalizedValue)) {
      const [integerPart, decimalPart] = normalizedValue?.split(".");
      const formattedIntegerPart = parseInt(integerPart.replace(/,/g, "")).toLocaleString("en-GB");
      return decimalPart !== undefined ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;
    }
  }
  return value;
};

// Function to convert comma-separated string to plain number
export const convertCommasToNumber = (value: string) => {
  return value.replace(/,/g, "");
};

/**
 * Function to check if a string contains any non-numeric characters
 * including both full-width and half-width characters.
 *z
 * @param {string} input - The input string to validate.
 * @returns {boolean} - Returns true if non-numeric characters are found, otherwise false.
 */
export const containsNonNumeric = (input: string): boolean => {
  const moji = Moji(input);

  // Normalize the string to half-width for consistency
  const normalized = moji.convert("ZE", "HE").toString();

  // Check for non-numeric characters using regex
  const nonNumericRegex = /[^0-9.]/; // Only allows half-width numeric digits (0-9)
  return nonNumericRegex.test(normalized);
};

/**
 * Area-code prefixes for special service numbers (toll-free / Navi Dial / Teledome, etc.).
 * These are segmented differently from regular landline/mobile numbers: split on a 4-digit prefix.
 * - 0120 / 0570 / 0180 / 0990: 10 digits → 4-3-3 (e.g. 0120-123-456)
 * - 0800: 11 digits → 4-3-4 (e.g. 0800-123-4567)
 * Ref: http://www.denpa-data.com/annex/telephone/01.htm
 */
const SPECIAL_TEL_PREFIXES = ["0120", "0570", "0180", "0990", "0800"];

export const convertToPhoneNumber = (phone: string): string => {
  if (!phone) return "";

  const digits = phone.replace(/\D/g, "");

  // Special numbers split as 4-digit prefix + 3 digits + remainder (prevents incorrect segmentation)
  if ((digits.length === 10 || digits.length === 11) && SPECIAL_TEL_PREFIXES.some((prefix) => digits.startsWith(prefix))) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return phone;
};
